// src/lib/analytics-hooks.ts
//
// React hooks for the two custom events whose trigger is a lifecycle rather
// than a click. Both route through trackAnalyticsEvent, which enforces the
// enablement rule, private-route blocking, ga-disable, and the closed
// parameter allowlist.

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { trackAnalyticsEvent } from '@/lib/analytics'

/**
 * Fires clinician_profile_view once per profile-modal opening: the effect
 * runs on the closed→open transition only (boolean dependency, so re-renders
 * and member-object identity churn cannot re-fire it), and firing again
 * requires the modal to close and reopen. No clinician identity is ever sent.
 */
export function useTrackClinicianProfileView(open: boolean): void {
  useEffect(() => {
    if (!open) return
    trackAnalyticsEvent('clinician_profile_view', {
      content_type: 'clinician_profile',
    })
  }, [open])
}

const VISIBLE_TIME_TARGET_MS = 45_000
const SCROLL_DEPTH_TARGET = 0.5
const TICK_MS = 1_000

/**
 * service_page_engagement: fires once per page view when cumulative VISIBLE
 * time reaches 45s AND max scroll depth reaches 50%. Time is only counted
 * while document.visibilityState === 'visible', so background tabs never
 * accumulate. State fully resets on route navigation (pathname effect), and
 * oncePerPageView dedup in the helper is a second guard.
 */
export function useServicePageEngagement(): void {
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === 'undefined') return

    let fired = false
    let visibleMs = 0
    let visibleSince: number | null =
      document.visibilityState === 'visible' ? Date.now() : null
    let maxDepth = 0

    const currentDepth = (): number => {
      const scrollable = document.documentElement.scrollHeight
      if (scrollable <= 0) return 0
      return Math.min(1, (window.scrollY + window.innerHeight) / scrollable)
    }

    const accumulateVisibleTime = () => {
      if (visibleSince !== null) {
        const now = Date.now()
        visibleMs += now - visibleSince
        visibleSince = now
      }
    }

    const evaluate = () => {
      if (fired) return
      accumulateVisibleTime()
      maxDepth = Math.max(maxDepth, currentDepth())
      if (
        visibleMs >= VISIBLE_TIME_TARGET_MS &&
        maxDepth >= SCROLL_DEPTH_TARGET
      ) {
        fired = true
        trackAnalyticsEvent(
          'service_page_engagement',
          { content_type: 'service_page' },
          { oncePerPageView: true },
        )
      }
    }

    const onScroll = () => evaluate()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        visibleSince = Date.now()
      } else {
        accumulateVisibleTime()
        visibleSince = null
      }
    }

    const interval = window.setInterval(evaluate, TICK_MS)
    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('visibilitychange', onVisibilityChange)
    evaluate() // short pages can satisfy the depth condition immediately

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [pathname])
}
